'use client';

import { CustomError } from '@core/utils';
import { usePathname } from 'next/navigation';
import { useMemo, useState } from 'react';

import type { Comment } from '@/api/getCommentList';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';

import {
  scrollToCommentTarget,
  useCommentPostSuccess,
  useCommentThreadHashScroll,
} from './_hooks/useCommentThreadEffects';
import { useCommentThreadNavigation } from './_hooks/useCommentThreadNavigation';
import {
  CommentField,
  type CommentFieldProps,
  type CommentFieldSubmitResult,
} from './CommentField';
import type { CommentListProps } from './CommentList';
import { CommentList } from './CommentList';

type CommentFieldOptions = Pick<
  CommentFieldProps,
  'className' | 'sticky' | 'topDivider' | 'topSlot' | 'withPortal'
>;

interface ReplyMode {
  /** 답글이 어느 최상위 댓글 트리 아래에 달리는지 식별한다. */
  rootCommentId: number;
  /** 실제 API 전송 시 부모 댓글로 들어가는 id다. */
  parentCommentId: number;
  /** 입력창 상단 안내 문구와 멘션 prefix 표시에 사용한다. */
  replyTo: string;
}

interface EditMode {
  /** 수정 대상으로 선택된 댓글 id다. */
  commentId: number;
  /** composer에 미리 주입할 원본 댓글 내용이다. */
  contents: string;
}

export interface CommentThreadProps extends Pick<
  CommentListProps,
  'commentType' | 'contentId' | 'initialData' | 'rootCommentId' | 'className'
> {
  /** 글 제목 */
  title?: string;
  /** 정책 이름 */
  policyName?: string;
  /** 첫 렌더 시 입력창에 포커스를 줄지 여부다. */
  initOnFocus?: boolean;
  /** 리스트만 쓰는 화면을 위해 입력창 렌더 여부를 분리한다. */
  hasCommentField?: boolean;
  /** 화면별 sticky/slot 차이를 주입하기 위한 CommentField 옵션 묶음이다. */
  commentFieldProps?: CommentFieldOptions;
}

export const CommentThread = ({
  initOnFocus,
  commentType,
  contentId,
  title,
  policyName,
  initialData,
  rootCommentId,
  className,
  hasCommentField = true,
  commentFieldProps,
}: CommentThreadProps) => {
  const pathname = usePathname();
  const isLogin = useAuthStore((state) => state.isLogin);
  const router = useCustomRouter();
  const [commentData, setCommentData] = useState(initialData);
  const [replyMode, setReplyMode] = useState<ReplyMode>();
  const [editMode, setEditMode] = useState<EditMode>();
  const [focusTrigger, setFocusTrigger] = useState(0);

  const { sortType, handleChangeSortType, resetSortType } =
    useCommentThreadNavigation();

  // URL hash로 댓글 영역에 진입한 경우 리스트 상단 위치를 재보정한다.
  useCommentThreadHashScroll(pathname ?? '');

  const handleCommentPostSuccess = useCommentPostSuccess({
    commentType,
    contentId,
    title,
    policyName,
    sortType,
    resetSortType,
  });

  const openComposer = () => {
    // 같은 reply/edit 모드를 다시 눌러도 focus effect가 한 번 더 돌도록 trigger를 올린다.
    setFocusTrigger((prev) => prev + 1);
  };

  const clearComposerMode = () => {
    setReplyMode(undefined);
    setEditMode(undefined);
  };

  const requireLogin = () => {
    if (isLogin) return false;

    const url = new URL(window.location.href);
    router.push(
      ROUTES.LOGIN.withSearchParams({
        searchParams: { redirect: `${url.pathname}${url.search}` },
      }),
    );

    return true;
  };

  const handleClickReplyButton = (
    comment: Comment,
    nextRootCommentId: number,
    nextParentCommentId: number,
  ) => {
    if (requireLogin()) return;

    // 답글과 수정은 동시에 성립할 수 없어서 진입 시 반대 모드를 항상 비운다.
    setEditMode(undefined);
    setReplyMode({
      rootCommentId: nextRootCommentId,
      parentCommentId: nextParentCommentId,
      replyTo: comment.nickname,
    });
    openComposer();
  };

  const handleClickEditButton = (comment: Comment) => {
    if (requireLogin()) return;

    // 수정은 별도 페이지 대신 하단 composer에 현재 댓글 내용을 그대로 주입한다.
    setReplyMode(undefined);
    setEditMode({
      commentId: comment.commentId,
      contents: comment.contents,
    });
    openComposer();
  };

  const composerTopSlot = useMemo(() => {
    const contextualSlot =
      replyMode || editMode ? (
        <section className="flex items-center justify-between gap-12 rounded-small bg-Gray-50 px-12 py-8">
          <div className="min-w-0 flex-1">
            <strong className="block truncate font-600 text-Gray-900 typo-caption1">
              {editMode ? '댓글 수정 중' : '답글 작성 중'}
            </strong>
            <p className="truncate text-Gray-700 typo-caption1">
              {editMode
                ? '수정 후 전송 버튼을 누르면 댓글이 바로 반영돼요.'
                : `@${replyMode?.replyTo}님에게 답글을 남기고 있어요.`}
            </p>
          </div>
          <button
            className="shrink-0 font-500 text-Gray-700 typo-caption1"
            type="button"
            onClick={clearComposerMode}
          >
            취소
          </button>
        </section>
      ) : null;

    if (!commentFieldProps?.topSlot && !contextualSlot) return undefined;

    // 기존 정책 액션 슬롯과 답글/수정 상태 안내를 같은 sticky 영역 안에서 합친다.
    return (
      <div className="flex flex-col gap-12">
        {commentFieldProps?.topSlot}
        {contextualSlot}
      </div>
    );
  }, [commentFieldProps?.topSlot, editMode, replyMode]);

  const shouldRenderCommentField =
    hasCommentField && (composerTopSlot !== undefined || isLogin);

  const handleFieldSuccess = (result: CommentFieldSubmitResult) => {
    // 수정은 목록 데이터 갱신 후 같은 위치로 다시 스크롤만 맞추면 된다.
    if (result.type === 'edit') {
      clearComposerMode();
      scrollToCommentTarget(result.commentId);

      return;
    }

    if (!result.response) throw new CustomError();

    const point = result.response.pointActivity?.resultYN
      ? result.response.pointActivity.addPoints
      : undefined;

    // 작성/답글은 서버 응답의 신규 댓글 id 기준으로 후속 스크롤과 토스트를 처리한다.
    clearComposerMode();
    handleCommentPostSuccess(result.response.welloCommentId, point);
  };

  return (
    <>
      <CommentList
        className={className}
        commentType={commentType}
        contentId={contentId}
        initialData={commentData}
        rootCommentId={rootCommentId}
        sortType={sortType}
        onChangeCommentData={setCommentData}
        onChangeSortType={handleChangeSortType}
        onClickEditButton={handleClickEditButton}
        onClickReplyButton={handleClickReplyButton}
      />
      {shouldRenderCommentField ? (
        <CommentField
          commentType={commentType}
          contentId={contentId}
          initOnFocus={initOnFocus}
          parentCommentId={replyMode?.parentCommentId}
          replyTo={replyMode?.replyTo}
          rootCommentId={replyMode?.rootCommentId ?? rootCommentId}
          editingCommentId={editMode?.commentId}
          editingContents={editMode?.contents}
          focusTrigger={focusTrigger || undefined}
          {...commentFieldProps}
          topSlot={composerTopSlot}
          onSuccess={handleFieldSuccess}
        />
      ) : null}
    </>
  );
};
